import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { logMount } from "@/lib/spa-debug";

type AppRole = "primary_owner" | "owner" | "agent" | "user";

interface ImpersonationState {
  active: boolean;
  logId: string | null;
  targetUserId: string | null;
  targetRole: AppRole | null;
  targetDisplayName: string | null;
  targetAvatarUrl: string | null;
  targetEmail: string | null;
  targetTrialExpiresAt: string | null;
  targetSubscriptionPlan: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  displayName: string | null;
  avatarUrl: string | null;
  trialExpiresAt: string | null;
  subscriptionPlan: string | null;
  accountStatus: string | null;
  isTrialActive: boolean;
  /** True when role=user, no subscription, and trial_expires_at <= now */
  isTrialExpired: boolean;
  /** True when the user's most-recent subscription was refunded (full refund) */
  isRefunded: boolean;
  /**
   * True when role=user and the most-recent subscription is in an expired-like
   * state (expired/canceled/unpaid/past_due) past its current period end.
   * Owners, primary owners and admins are never marked expired.
   */
  isSubscriptionExpired: boolean;
  /**
   * Single source of truth used by the global access guard. True when a seller
   * has neither an active subscription nor an active trial — they should only
   * be able to see /pricing.
   */
  isAccessLocked: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  // Impersonation
  impersonation: ImpersonationState;
  startImpersonation: (targetUserId: string) => Promise<void>;
  endImpersonation: (reason?: string) => Promise<void>;
  /** True when impersonation is active OR the seller's trial has expired — all writes should be blocked */
  isReadOnly: boolean;
  // Effective values (respects impersonation)
  effectiveRole: AppRole | null;
  effectiveDisplayName: string | null;
  effectiveAvatarUrl: string | null;
  effectiveUserId: string | null;
  effectiveTrialExpiresAt: string | null;
  effectiveSubscriptionPlan: string | null;
}

const defaultImpersonation: ImpersonationState = {
  active: false,
  logId: null,
  targetUserId: null,
  targetRole: null,
  targetDisplayName: null,
  targetAvatarUrl: null,
  targetEmail: null,
  targetTrialExpiresAt: null,
  targetSubscriptionPlan: null,
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  role: null,
  displayName: null,
  avatarUrl: null,
  trialExpiresAt: null,
  subscriptionPlan: null,
  accountStatus: null,
  isTrialActive: false,
  isTrialExpired: false,
  isRefunded: false,
  isSubscriptionExpired: false,
  isAccessLocked: false,
  loading: true,
  signOut: async () => {},
  refreshProfile: async () => {},
  impersonation: defaultImpersonation,
  startImpersonation: async () => {},
  endImpersonation: async () => {},
  isReadOnly: false,
  effectiveRole: null,
  effectiveDisplayName: null,
  effectiveAvatarUrl: null,
  effectiveUserId: null,
  effectiveTrialExpiresAt: null,
  effectiveSubscriptionPlan: null,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [trialExpiresAt, setTrialExpiresAt] = useState<string | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<string | null>(null);
  const [refundedAt, setRefundedAt] = useState<string | null>(null);
  const [latestSubStatus, setLatestSubStatus] = useState<string | null>(null);
  const [latestSubPeriodEnd, setLatestSubPeriodEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<ImpersonationState>(defaultImpersonation);

  const isTrialActive = trialExpiresAt ? new Date(trialExpiresAt) > new Date() : false;

  const fetchUserMeta = async (userId: string): Promise<AppRole | null> => {
    try {
      const [{ data: roleData }, { data: profileData }, { data: subData }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", userId).maybeSingle(),
        supabase.from("profiles").select("display_name, avatar_url, trial_expires_at, subscription_plan, status").eq("user_id", userId).maybeSingle(),
        supabase
          .from("subscriptions")
          .select("refunded_at, status, current_period_end")
          .eq("user_id", userId)
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const resolvedRole = (roleData?.role as AppRole) || null;
      setRole(resolvedRole);
      setDisplayName(profileData?.display_name || null);
      setAvatarUrl(profileData?.avatar_url || null);
      setTrialExpiresAt(profileData?.trial_expires_at || null);
      setSubscriptionPlan(profileData?.subscription_plan || null);
      setAccountStatus((profileData as any)?.status || null);
      setRefundedAt(
        subData && typeof subData === 'object' && 'refunded_at' in subData
          ? (subData as any).refunded_at
          : null
      );
      setLatestSubStatus(
        subData && typeof subData === 'object' && 'status' in subData
          ? (subData as any).status
          : null
      );
      setLatestSubPeriodEnd(
        subData && typeof subData === 'object' && 'current_period_end' in subData
          ? (subData as any).current_period_end
          : null
      );
      return resolvedRole;
    } finally {
      setLoading(false);
    }
  };

  const syncImpersonationState = async () => {
    try {
      // Guard: impersonate requires a real authenticated user JWT.
      // Without a fresh access token, invoke would fall back to the anon
      // publishable key and the function (correctly) returns 401.
      const { data: fresh } = await supabase.auth.getSession();
      const accessToken = fresh?.session?.access_token;
      if (!accessToken) {
        setImpersonation(defaultImpersonation);
        return;
      }

      const { data, error } = await supabase.functions.invoke("impersonate", {
        headers: { Authorization: `Bearer ${accessToken}` },
        body: { action: "status" },
      });

      if (error || data?.error || !data?.active) {
        setImpersonation(defaultImpersonation);
        return;
      }

      setImpersonation({
        active: true,
        logId: data.log_id || null,
        targetUserId: data.target?.user_id || null,
        targetRole: (data.target?.role as AppRole) || null,
        targetDisplayName: data.target?.display_name || null,
        targetAvatarUrl: data.target?.avatar_url || null,
        targetEmail: data.target?.email || null,
        targetTrialExpiresAt: data.target?.trial_expires_at || null,
        targetSubscriptionPlan: data.target?.subscription_plan || null,
      });
    } catch {
      setImpersonation(defaultImpersonation);
    }
  };


  useEffect(() => {
    logMount("AuthProvider");
    let mounted = true;
    let initialized = false;

    // 1. Initial session fetch — only source of truth for first load
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      setUser(s?.user ?? null);

      if (s?.user) {
        const resolvedRole = await fetchUserMeta(s.user.id);
        if (!mounted) return;
        // Check if account is disabled (sellers only)
        if (accountStatus && accountStatus.startsWith("disabled") && resolvedRole !== "primary_owner" && resolvedRole !== "owner" && resolvedRole !== "agent") {
          await supabase.auth.signOut();
          setUser(null); setSession(null); setRole(null);
          setLoading(false);
          return;
        }
        await syncImpersonationState();
        // Track login + activity
        supabase.rpc("update_last_login" as any).then(() => {});
        supabase.rpc("increment_daily_usage" as any, { _user_id: s.user.id, _is_action: false }).then(() => {});
        initialized = true;
      } else {
        setLoading(false);
        setImpersonation(defaultImpersonation);
        initialized = true;
      }
    });

    // 2. Listen only for real auth changes (sign-in, sign-out, password recovery)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted || !initialized) return;

        // Ignore background events that don't represent a real auth change
        if (event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION") return;

        if (event === "SIGNED_OUT") {
          setSession(null);
          setUser(null);
          setRole(null);
          setDisplayName(null);
          setAvatarUrl(null);
          setTrialExpiresAt(null);
          setSubscriptionPlan(null);
          setAccountStatus(null);
          setRefundedAt(null);
          setLatestSubStatus(null);
          setLatestSubPeriodEnd(null);
          setImpersonation(defaultImpersonation);
          setLoading(false);
          return;
        }

        // SIGNED_IN, PASSWORD_RECOVERY, USER_UPDATED
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          // Don't set loading=true on subsequent auth events to avoid remounting the dashboard
          fetchUserMeta(session.user.id).then(() => {
            if (!mounted) return;
            syncImpersonationState();
          });
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const refreshProfile = async () => {
    if (!user) return;
    await fetchUserMeta(user.id);
    await syncImpersonationState();
  };

  const startImpersonation = async (targetUserId: string) => {
    if (role !== "owner" && role !== "primary_owner" && role !== "agent") return;
    try {
      const { data: fresh } = await supabase.auth.getSession();
      const accessToken = fresh?.session?.access_token;
      if (!accessToken) throw new Error("Your session expired — please sign in again.");
      const { data, error } = await supabase.functions.invoke("impersonate", {
        headers: { Authorization: `Bearer ${accessToken}` },
        body: { action: "start", target_user_id: targetUserId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setImpersonation({
        active: true,
        logId: data.log_id,
        targetUserId,
        targetRole: data.target.role as AppRole,
        targetDisplayName: data.target.display_name,
        targetAvatarUrl: data.target.avatar_url,
        targetEmail: data.target.email,
        targetTrialExpiresAt: data.target.trial_expires_at,
        targetSubscriptionPlan: data.target.subscription_plan,
      });
    } catch (err: any) {
      throw err;
    }
  };

  const endImpersonation = async (reason?: string) => {
    try {
      const { data: fresh } = await supabase.auth.getSession();
      const accessToken = fresh?.session?.access_token;
      if (!accessToken) {
        setImpersonation(defaultImpersonation);
        return;
      }
      const authHeaders = { Authorization: `Bearer ${accessToken}` };
      // Dedicated endpoint: atomically closes every open impersonation row
      // for the caller and writes a security audit entry. Falls back to the
      // legacy `impersonate?action=end` path only if the dedicated function
      // is unreachable, so we never leave a hidden session open.
      const { error } = await supabase.functions.invoke("impersonate-end", {
        headers: authHeaders,
        body: { reason: reason ?? "user_initiated" },
      });
      if (error) {
        await supabase.functions.invoke("impersonate", {
          headers: authHeaders,
          body: { action: "end", log_id: impersonation.logId, reason },
        });
      }
    } catch {
      try {
        const { data: fresh } = await supabase.auth.getSession();
        const accessToken = fresh?.session?.access_token;
        if (accessToken) {
          await supabase.functions.invoke("impersonate", {
            headers: { Authorization: `Bearer ${accessToken}` },
            body: { action: "end", log_id: impersonation.logId, reason },
          });
        }
      } catch {
        // no-op
      }
    } finally {
      setImpersonation(defaultImpersonation);
    }
  };

  const signOut = async () => {
    try {
      const { data: fresh } = await supabase.auth.getSession();
      const accessToken = fresh?.session?.access_token;
      if (accessToken) {
        await supabase.functions.invoke("impersonate", {
          headers: { Authorization: `Bearer ${accessToken}` },
          body: { action: "end" },
        });
      }
    } catch {
      // no-op
    }


    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
    setDisplayName(null);
    setAvatarUrl(null);
    setTrialExpiresAt(null);
    setSubscriptionPlan(null);
    setAccountStatus(null);
    setRefundedAt(null);
    setLatestSubStatus(null);
    setLatestSubPeriodEnd(null);
    setImpersonation(defaultImpersonation);
  };

  // Effective values: when impersonating, show target's data; otherwise show own
  const effectiveRole = impersonation.active ? impersonation.targetRole : role;
  const effectiveDisplayName = impersonation.active ? impersonation.targetDisplayName : displayName;
  const effectiveAvatarUrl = impersonation.active ? impersonation.targetAvatarUrl : avatarUrl;
  const effectiveUserId = impersonation.active ? impersonation.targetUserId : user?.id || null;
  const effectiveTrialExpiresAt = impersonation.active ? impersonation.targetTrialExpiresAt : trialExpiresAt;
  const effectiveSubscriptionPlan = impersonation.active ? impersonation.targetSubscriptionPlan : subscriptionPlan;

  // Trial expiry — only applies to sellers (role=user) without an active subscription.
  // Evaluated against EFFECTIVE values so impersonation correctly mirrors the
  // target seller's trial state.
  const isTrialExpired = (() => {
    if (effectiveRole !== "user") return false;
    if (effectiveSubscriptionPlan) return false;
    if (!effectiveTrialExpiresAt) return false;
    return new Date(effectiveTrialExpiresAt) <= new Date();
  })();

  // Subscription expiry — only applies to sellers (role=user). True when the
  // most-recent subscription row is in an expired-like state past its period
  // end. Mirrors the DB-side `is_subscription_expired` helper.
  //
  // NOTE: When impersonating we cannot read the target's subscription rows
  // from this client (RLS), so we conservatively only rely on `isTrialExpired`
  // (which uses target trial fields exposed by the impersonate function).
  const EXPIRED_LIKE = new Set(["expired", "canceled", "unpaid", "past_due", "trial_expired"]);
  const isSubscriptionExpired = (() => {
    if (impersonation.active) return false;
    if (effectiveRole !== "user") return false;
    if (!latestSubStatus) return false;
    if (!EXPIRED_LIKE.has(latestSubStatus)) return false;
    if (latestSubPeriodEnd && new Date(latestSubPeriodEnd) > new Date()) return false;
    return true;
  })();

  // Single source of truth for the global access lock. Sellers only.
  // The lock is enforced for BOTH direct logins AND impersonation sessions —
  // an admin impersonating an expired seller MUST see the same locked
  // experience the seller would see, otherwise we ship blind.
  const isAccessLocked =
    effectiveRole === "user" &&
    (isSubscriptionExpired || isTrialExpired);

  // Refunded — true when the user's most-recent subscription has a refund flag and no active plan.
  // Always false during impersonation (we show the target's view, not the impersonator's billing state).
  const isRefunded = !impersonation.active && !!refundedAt && !subscriptionPlan;

  // Read-only when impersonating OR when trial expired OR when refunded.
  const isReadOnly = impersonation.active || isTrialExpired || isRefunded;

  return (
    <AuthContext.Provider value={{
      user, session, role, displayName, avatarUrl, trialExpiresAt, subscriptionPlan, accountStatus, isTrialActive, isTrialExpired,
      isRefunded, isSubscriptionExpired, isAccessLocked,
      loading, signOut, refreshProfile,
      impersonation, startImpersonation, endImpersonation, isReadOnly,
      effectiveRole, effectiveDisplayName, effectiveAvatarUrl, effectiveUserId,
      effectiveTrialExpiresAt, effectiveSubscriptionPlan,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
